import React, { useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useQueryParam } from "use-query-params";

import {
  OrderDirection,
  ProductOrder,
  ProductOrderField,
  StockAvailability,
} from "../../../../__generated__/globalTypes";
import Switch from "react-switch";

import ArrowDown from "../../../images/icons/arrow-down-solid.svg";
import ArrowUp from "../../../images/icons/arrow-up-solid.svg";
import SortDown from "../../../images/icons/sort-down-solid.svg";
import SortUp from "../../../images/icons/sort-up-solid.svg";
import { useOutsideHandler } from "@src/core/utils";
import { productsVariables } from "@components/ProductPage/ProductList/__generated__/products";

import { styled } from "@styles";
import { Visibility } from "@components/Visibility";

const Wrapper = styled.div`
  display: inline;
  font-size: 0.9rem;
`;

const GraySpan = styled.span`
  color: #8a8a8a;
  white-space: pre;
`;

const B = styled.b`
  margin-right: 0.5rem;
  font-weight: 530;
`;

const Ul = styled.ul`
  z-index: 999;
  position: absolute;
  background: #f8f8f8;
  box-shadow: 3px 3px 3px 0 hsla(20, 3%, 82%, 0.75);
  width: 20rem;
  list-style: none;
  top: 2.5rem;
  right: 3rem;
  padding: 1rem 2rem;
`;

const P = styled.div`
  cursor: pointer;

  :hover {
    text-decoration: underline;
  }

  margin-bottom: 0;
`;

const FloatRightFA = styled.div`
  float: right;
`;

const DivSpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
  padding-left: 1rem;
  padding-right: 1rem;
  margin-bottom: 1rem;
`;

const DivInline = styled.div`
  display: inline;
`;

export const SortingParam = {
  encode: (productOrder: ProductOrder) => {
    const direction = productOrder.direction;
    const directionCharacter = direction === OrderDirection.ASC ? "" : "-";
    const field = productOrder.field.toLowerCase();
    return `${directionCharacter}${field}`;
  },
  decode: (value: string) => {
    if (!!value) {
      value = value.toUpperCase();
      let field = null;
      let direction = null;
      if (value[0] === "-") {
        direction = OrderDirection.DESC;
        const strField: string = value.slice(1);
        field = (ProductOrderField as any)[strField];
      } else {
        direction = OrderDirection.ASC;
        field = (ProductOrderField as any)[value];
      }
      return {
        field: field as ProductOrderField,
        direction: direction as OrderDirection,
      } as ProductOrder;
    } else {
      return {
        field: ProductOrderField.POPULARITY,
        direction: OrderDirection.DESC,
      } as ProductOrder;
    }
  },
};

interface IProps {
  initialVariables: productsVariables;
  setVariables: any;
  numOfProducts: number;
}

const SortingFilter: React.FC<IProps> = ({
  initialVariables,
  setVariables,
  numOfProducts,
}: IProps) => {
  const productOrderFields = {
    POPULARITY: "POPULARITY",
    NAME: "NAME",
    PRICE: "PRICE",
    DATE: "LAST UPDATED",
  };
  const [showFilter, setShowFilter] = useState(false);
  const [sortBy, setSortBy] = useQueryParam("sort_by", SortingParam);
  const getSortVariables = (productOrder: ProductOrder) => {
    return { sortBy: productOrder };
  };
  useEffect(() => {
    setVariables((prev) => {
      return { ...prev, ...getSortVariables(sortBy) };
    });
  }, [JSON.stringify(sortBy)]);

  const handleClickSorting = (
    field: ProductOrderField,
    direction: OrderDirection
  ) => {
    setShowFilter(false);
    const productOrder = { field, direction };
    setSortBy(productOrder);
  };
  const handleClickShow = () => {
    setShowFilter((prevState) => !prevState);
  };
  const wrapperRef = useRef(null);
  useOutsideHandler(wrapperRef, () => setShowFilter(false));

  const [toggleValue, setToggleValue] = useState(
    initialVariables.stockAvailability === StockAvailability.IN_STOCK
  );

  // in case a different collection page is clicked in navigation bar
  // toggle switch should be in an initial state
  useEffect(() => {
    setToggleValue(
      initialVariables.stockAvailability === StockAvailability.IN_STOCK
    );
  }, [JSON.stringify(initialVariables)]);

  const handleToggle = (toggleValue) => {
    setToggleValue(toggleValue);
    const stockVariable = {
      stockAvailability: toggleValue ? StockAvailability.IN_STOCK : null,
    };
    setVariables((prev) => {
      return { ...prev, ...stockVariable };
    });
  };

  return (
    <Wrapper>
      <DivSpaceBetween>
        <DivInline>
          <Switch
            // https://react-switch.netlify.app/
            checked={toggleValue}
            onChange={handleToggle}
            handleDiameter={22}
            onColor="#fd346a"
            height={27}
            width={85}
            borderRadius={6}
            uncheckedIcon={
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  fontSize: 14,
                  color: "white",
                  width: 20,
                }}
              >
                {`All`}
              </div>
            }
            checkedIcon={
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  fontSize: 14,
                  color: "white",
                  width: 60,
                  paddingLeft: 5,
                }}
              >
                {`In stock`}
              </div>
            }
          />
          <Visibility
            child={<GraySpan>{`  ${numOfProducts} products`}</GraySpan>}
            visible={0 < numOfProducts}
          />
        </DivInline>
        <DivInline ref={wrapperRef}>
          <button onClick={handleClickShow}>
            <B>{productOrderFields[sortBy.field]}</B>
            {sortBy.direction === OrderDirection.ASC ? (
              <img src={SortUp} width={14} />
            ) : (
              <img src={SortDown} width={14} />
            )}
          </button>
          {showFilter && (
            <Ul>
              {Object.entries(productOrderFields).map(([key, field], i) => {
                return (
                  <li key={i}>
                    <Container>
                      <Row>
                        <Col xs={6}>
                          Sort by: <b>{field.toLowerCase()}</b>
                        </Col>
                        <Col xs={6}>
                          <P
                            onClick={() =>
                              handleClickSorting(
                                key as ProductOrderField,
                                OrderDirection.ASC
                              )
                            }
                          >
                            ascending
                            <FloatRightFA>
                              <img src={ArrowUp} width={14} />
                            </FloatRightFA>
                          </P>
                          <P
                            onClick={() =>
                              handleClickSorting(
                                key as ProductOrderField,
                                OrderDirection.DESC
                              )
                            }
                          >
                            descending
                            <FloatRightFA>
                              <img src={ArrowDown} width={14} />
                            </FloatRightFA>
                          </P>
                        </Col>
                      </Row>
                    </Container>
                    {i < Object.keys(productOrderFields).length - 1 ? (
                      <hr />
                    ) : (
                      ""
                    )}
                  </li>
                );
              })}
            </Ul>
          )}
        </DivInline>
      </DivSpaceBetween>
    </Wrapper>
  );
};

export { SortingFilter };
